/* eslint-disable no-underscore-dangle */
import _ from 'lodash'
import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import classnames from 'classnames'

import { reviewOrderSelector as stateSelector } from 'reduxModules/checkout/selectors'
import { reviewOrderDispatchers as mergeProps } from 'reduxModules/checkout/dispatchers'
import initPayPalFlow from 'reduxModules/utils/paypalFlow'
import { cartPropTypes } from 'components/Cart'
import { deliveryMethodsPropTypes } from 'components/Checkout/DeliveryMethods'
import { addressProptypes } from 'components/Account/Addresses/AddressList/Address'
import addFraudProtection from 'components/utils/addFraudProtection'

import withAsyncScriptLoader from '../../hooks/useScriptAsync'
import withRouterV6 from '../../utils/withRouterV6'
import utils from '../utils/cartItemUtils'
import PaymentMethodDetails from '../generic/PaymentMethodDetails'
import AddressDetails from '../generic/AddressDetails'
import keyUpHandler from '../utils/keyUpHandler'
import math from '../../redux/modules/utils/math'

import './styles.css'

export class ReviewOrder extends Component {
  constructor(props) {
    super(props)
    this.state = {
      proceedToSubscribe: false,
      initPayPalFlow: true
    }
    this.isPlacingOrder = false
    this.proceedPlaceOrder = this.proceedPlaceOrder.bind(this)
    this.proceedPlaceSubscription = this.proceedPlaceSubscription.bind(this)
    this._initPayPalFlow = this._initPayPalFlow.bind(this)
  }

  componentDidMount() {
    this.props.hideInnerNav()
    const { clientToken, getBraintreeClientToken, isLoggedIn } = this.props
    if (_.isEmpty(clientToken) && isLoggedIn) getBraintreeClientToken()
  }

  componentDidUpdate() {
    const {
      clientToken,
      isSubscribing,
      payWithCreditCard,
      showFraudProtectionError,
      useExistentPaymentMethod
    } = this.props
    const plan = isSubscribing ? this.props.planSelected.id : null

    // PayPal flow
    if (clientToken && !payWithCreditCard && !useExistentPaymentMethod)
      addFraudProtection(
        this._initPayPalFlow,
        [plan],
        clientToken,
        showFraudProtectionError
      )
  }

  componentWillUnmount() {
    const { showInnerNav, clearCheckout } = this.props
    // Only clear checkout if user is aborting (not placing an order)
    // When placing an order, clearOrderAction is called by resolveSuccessfulSettling
    if (!this.isPlacingOrder)
      clearCheckout()
    showInnerNav()
  }

  _initPayPalFlow(deviceData, plan) {
    const {
      clientToken,
      isSubscribing,
      order,
      orderTotal,
      placePayPalOrder,
      placePayPalSubscription,
      usedFreeTrial,
      userEmail
    } = this.props
    const handler = isSubscribing ? placePayPalSubscription : placePayPalOrder
    initPayPalFlow(
      clientToken,
      orderTotal,
      handler,
      order,
      isSubscribing,
      plan,
      usedFreeTrial,
      deviceData,
      userEmail
    )
  }

  proceedPlaceOrder() {
    this.isPlacingOrder = true
    this.props.placeOrder(this.props.order)
  }

  proceedPlaceSubscription(planId) {
    this.isPlacingOrder = true
    this.props.placeSubscription(
      this.props.order,
      planId,
      this.props.usedFreeTrial,
      this.props.userEmail
    )
  }

  render() {
    const { props } = this
    const isSubscribing = props.isSubscribing && !_.isEmpty(props.planSelected)
    const plan = props.planSelected

    return (
      <div id="ReviewOrder">
        <div className="title wrapper">
          <h1>Review your {isSubscribing ? 'Subscription' : 'Order'}</h1>
        </div>
        <div className="wrapper">
          <div className="row">
            {!props.orderIsDigital && (
              <div className="ship-to-summary">
                <div className="header">
                  <h2>Ship to</h2>
                  <button
                    className="edit-address-button"
                    onClick={() => props.cancelCompleteShippingInfo(
                      props.useExistentAddress,
                      props.order.shippingAddress
                    )}
                    onKeyUp={event => keyUpHandler(
                      event,
                      props.cancelCompleteShippingInfo,
                      null,
                      null,
                      [props.useExistentAddress, props.order.shippingAddress]
                    )}
                    tabIndex="0"
                  >
                    Edit
                  </button>
                </div>
                {!props.orderIsDigital && props.order.shippingAddress && (
                  <div className="body">
                    <AddressDetails {...props.order.shippingAddress} />
                    <div className="delivery-date">
                      <span>Delivery details: </span>
                      {props.deliveryMethodDetails.name} {props.deliveryMethodDetails.description}
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className={classnames('bill-to-summary', { digital: props.orderIsDigital })}>
              <div className="header">
                <h2>{isSubscribing ? (plan.rank < 3 ? 'Bill  Monthly to' : 'Bill Yearly to') : 'Bill to'}</h2>
                <span
                  className="edit-address-button"
                  onClick={() => props.cancelCompleteBillingInfo(
                    props.order.billingAddress,
                    props.useExistentPaymentMethod,
                    props.payWithCreditCard
                  )}
                  onKeyUp={event => keyUpHandler(
                    event,
                    props.cancelCompleteShippingInfo,
                    null,
                    null,
                    [props.useExistentAddress, props.order.shippingAddress]
                  )}
                  tabIndex="0"
                  role="button"
                >
                  Edit
                </span>
              </div>
              <div className="body">
                {props.payWithCreditCard && props.paymentMethodDetails && (
                  <PaymentMethodDetails {...props.paymentMethodDetails} />
                )}
                {!this.props.payWithCreditCard && (
                  <div className="paypal-method-body">
                    <div className="paypal-icon">
                      <img src="https://assets.braintreegateway.com/payment_method_logo/paypal.png" alt="paypal" />
                    </div>
                    <div className="payment-method-description">
                      {!_.has(props.paymentMethodDetails, 'email') && (
                        <div className="paypal">Pay with PayPal</div>
                      )}
                      {_.has(props.paymentMethodDetails, 'email') && (
                        <div className="paypal">{props.paymentMethodDetails.email}</div>
                      )}
                    </div>
                  </div>
                )}
                {!_.isEmpty(props.order.billingAddress) && (
                  <>
                    {this.props.payWithCreditCard && (
                      <div className="billing-address-title">Billing address</div>
                    )}
                    <AddressDetails {...props.order.billingAddress} />
                  </>
                )}
              </div>
              {isSubscribing && (
                <div className="subscription-note">
                  <h3>AUTOMATIC RENEWAL NOTICE</h3>
                  <p>
                    By continuing, you agree that now and prior to each renewal cycle, Stan Winston School of Character Arts will charge your credit card/Paypal account the same price in effect for your current plan. You may cancel your subscription at any time by visiting your SWSCA account page. We value your membership and hope you enjoy learning from the Masters of Character Creation.
                  </p>
                </div>
              )}
            </div>
          </div>
          <div className="row">
            <div className="items">
              <div className="header">
                <h2>{isSubscribing ? 'Subscription Plan' : 'Items'}</h2>
              </div>
              <div className="items-body">
                {!isSubscribing && props.cart.cartItems.map((item) => (
                  <div className="item" key={item.id}>
                    <div className="thumbnail">
                      {item.type !== 'plan' && (
                        <img alt="Item image" src={item.thumbnail.cartThumb} />
                      )}
                      {item.type === 'physical' && (
                        <div className="quantity">{item.quantity}</div>
                      )}
                    </div>
                    <div className="column">
                      <div className="description">
                        <div className="type">
                          <span>{utils.getNaturalType(item.orderedModel, item.ordered)}</span>
                        </div>
                        <div className="title">
                          <span>{utils.getTitle(item.orderedModel, item.ordered)}</span>
                        </div>
                      </div>
                      <div className="price">
                        {props.isSubscribed && _.isNumber(item.regularPrice) && math.toFixed(item.regularPrice) > math.toFixed(item.price) && (
                          <span className="regular-price">${math.toFixed(item.regularPrice)}</span>
                        )}
                        <span>${math.toFixed(item.price * item.quantity)}</span>
                      </div>
                    </div>
                  </div>
                ))}
                {isSubscribing && (
                  <div className="item">
                    <div className="thumbnail">
                      <div className="plan-icon">
                        <i className={`icon-monster-${plan.rank}`} />
                      </div>
                    </div>
                    <div className="column">
                      <div className="description">
                        <div className="type">
                          <span>{utils.getNaturalType('plan', plan)}</span>
                        </div>
                        <div className="title">
                          <span>{utils.getTitle('plan', plan)}</span>
                        </div>
                        <div className="plan-description">
                          <span>{plan.description1}.</span>
                          <span>{plan.checkoutDescription2}.</span>
                          <span>{plan.description3}.</span>
                        </div>
                      </div>
                      <div className="price">
                        <span>${plan.price}{plan.rank < 3 ? '/Mo' : '/Yr'}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="filler">
              <div className="order-summary">
                <div className="header">
                  <h2>{isSubscribing ? 'Subscription summary' : 'Order summary'}</h2>
                </div>
                <div className="detailed-total">
                  <div className="subtitles">
                    {!props.isSubscribing && <span>Items total:</span>}
                    {this.props.discount && <span>Discount:</span>}
                    {!props.orderIsDigital && <span>Shipping:</span>}
                    {!props.isSubscribing && <span>Taxes:</span>}
                  </div>
                  <div className="values">
                    {!props.isSubscribing && <span>${props.cart.subtotal}</span>}
                    {this.props.discount && <span>- ${this.props.discount}</span>}
                    {!props.orderIsDigital && <span>${props.deliveryMethodDetails.cost}</span>}
                    {!props.isSubscribing && <span>${props.tax}</span>}
                  </div>
                </div>
                <div className={classnames('total', { subscribing: isSubscribing })}>
                  <div className="subtitles">
                    <span>{isSubscribing ? 'subscription total' : 'order total'}:</span>
                  </div>
                  <div className="values">
                    {!props.isSubscribing && <span>${props.orderTotal}</span>}
                    {props.isSubscribing && <span>${props.orderTotal}{plan.rank < 3 ? '/Mo' : '/Yr'}</span>}
                  </div>
                </div>
                <div className="place-order-button">
                  {(props.payWithCreditCard || props.useExistentPaymentMethod) && !props.isSubscribing && (
                    <button
                      onClick={() => this.proceedPlaceOrder()}
                      disabled={props.loading > 0}
                    >
                      Place my Order
                    </button>
                  )}
                  {(props.payWithCreditCard || props.useExistentPaymentMethod) && props.isSubscribing && (
                    <button
                      onClick={() => this.proceedPlaceSubscription(plan.id)}
                      disabled={this.props.loading > 0}
                    >
                      subscribe
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
}

ReviewOrder.propTypes = {
  cart: PropTypes.shape(cartPropTypes).isRequired,
  clientToken: PropTypes.string.isRequired,
  deliveryMethodDetails: PropTypes.shape(deliveryMethodsPropTypes),
  order: PropTypes.shape({
    billingAddress: PropTypes.shape(addressProptypes),
    delivery: PropTypes.shape({
      code: PropTypes.string
    }),
    payment: PropTypes.oneOfType([
      PropTypes.shape({ paymentMethodToken: PropTypes.string }),
      PropTypes.shape({ paymentMethodNonce: PropTypes.string })
    ]),
    shippingAddress: PropTypes.shape(addressProptypes)
  }).isRequired,
  planSelected: PropTypes.shape({
    id: PropTypes.number.isRequired,
    rank: PropTypes.number.isRequired
  }),
  orderTotal: PropTypes.string.isRequired,
  payWithCreditCard: PropTypes.bool.isRequired,
  placeOrder: PropTypes.func.isRequired,
  placePayPalOrder: PropTypes.func.isRequired,
  tax: PropTypes.number.isRequired,
  isSubscribing: PropTypes.bool.isRequired,
  placePayPalSubscription: PropTypes.func.isRequired,
  showInnerNav: PropTypes.func.isRequired,
  hideInnerNav: PropTypes.func.isRequired,
  clearCheckout: PropTypes.func.isRequired,
  router: PropTypes.shape([
    PropTypes.oneOfType([PropTypes.func, PropTypes.object])
  ]).isRequired,
  useExistentPaymentMethod: PropTypes.bool.isRequired,
  userEmail: PropTypes.string.isRequired,
  usedFreeTrial: PropTypes.bool.isRequired
}

ReviewOrder.defaultProps = {
  deliveryMethodDetails: {},
  planSelected: {}
}

export default withRouterV6(withAsyncScriptLoader([
  'https://www.paypalobjects.com/api/checkout.min.js',
  'https://js.braintreegateway.com/web/3.26.0/js/client.min.js',
  'https://js.braintreegateway.com/web/3.26.0/js/paypal-checkout.min.js'
])(connect(stateSelector, null, mergeProps)(ReviewOrder)))
